import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Alert, Autocomplete, Box, Card, Stack, TableContainer, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import {
  useCreateInternalPackageMutation,
  useDeleteInternalPackageMutation,
  useGetInternalFeatureModulesQuery,
  useGetInternalPackagesQuery,
  useGetInternalRolesQuery,
  useUpdateInternalPackageMutation,
} from '../../../../api/plans'
import { InternalListToolbar } from '../../../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../../../components/RowActions/RowActions'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { InternalPackage } from '../../types/Plans'
import { PackageEditDialog } from '../PackageEditDialog/PackageEditDialog'
import {
  filterPackages,
  formatRoleNames,
  getUnstoredPackageFields,
  packageFieldLabelKeys,
  packageTierLabelKeys,
  packageTypeLabelKeys,
  PackageFormValues,
  PackageRoleFilter,
  sortRoleNames,
  toPackageCreate,
  toPackageUpdate,
} from '../helpers/planHelpers'

type CustomProps = {
  onEdit: (planPackage: InternalPackage) => void
  onDelete: (planPackage: InternalPackage) => void
}

type DialogState = { open: boolean; planPackage?: InternalPackage }

type ConfirmData = { action: 'update'; planPackage: InternalPackage } | { action: 'delete'; planPackage: InternalPackage }

export const PackagesTab: FC = () => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const [searchParams, setSearchParams] = useSearchParams()
  const packagesQuery = useGetInternalPackagesQuery()
  const rolesQuery = useGetInternalRolesQuery()
  const modulesQuery = useGetInternalFeatureModulesQuery()
  const [createPackage, { isLoading: isCreating }] = useCreateInternalPackageMutation()
  const [updatePackage, { isLoading: isUpdating }] = useUpdateInternalPackageMutation()
  const [deletePackage] = useDeleteInternalPackageMutation()
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<ConfirmData>>()

  const packages = useMemo(() => packagesQuery.data ?? [], [packagesQuery.data])
  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data])
  const modules = useMemo(() => modulesQuery.data ?? [], [modulesQuery.data])
  const roleNames = useMemo(() => sortRoleNames(roles.map(({ name }) => name)), [roles])
  const selectedRole = searchParams.get('role') || undefined
  const roleFilter: PackageRoleFilter = searchParams.get('filter') === 'without' ? 'without' : 'with'
  const rows = useMemo(() => filterPackages(packages, selectedRole, roleFilter), [packages, selectedRole, roleFilter])

  const failureMessages = useCallback((key: string) => ({ error: t(key), errorWithReason: (reason: string) => `${t(key)}: ${reason}` }), [t])

  const setFilter = (role: string | null, filter: PackageRoleFilter) => {
    const next = new URLSearchParams(searchParams)
    if (role) next.set('role', role)
    else next.delete('role')
    if (filter === 'without') next.set('filter', 'without')
    else next.delete('filter')
    setSearchParams(next, { replace: true })
  }

  const tableColumns: GRTableColumn<InternalPackage, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:plans_package_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.name}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.name.toLowerCase(),
        // GRTable compares strings in reverse, so DESC lists names A to Z.
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 180 } },
      },
      {
        labelAccessor: t('internal-csm:plans_description'),
        accessor: ({ row }) => row.description,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 140 } },
      },
      {
        labelAccessor: t('internal-csm:plans_type'),
        accessor: ({ row }) => (packageTypeLabelKeys[row.type] ? t(packageTypeLabelKeys[row.type]) : '–'),
        sortable: true,
        sortAccessor: ({ row }) => row.type,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 130 } },
      },
      {
        labelAccessor: t('internal-csm:plans_tier'),
        accessor: ({ row }) => (packageTierLabelKeys[row.tier] ? t(packageTierLabelKeys[row.tier]) : '–'),
        sortable: true,
        sortAccessor: ({ row }) => row.tier,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 110 } },
      },
      {
        labelAccessor: t('internal-csm:plans_roles'),
        accessor: ({ row }) => formatRoleNames(row.roles) || t('internal-csm:plans_no_roles'),
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-csm:plans_monthly_price'),
        accessor: ({ row }) => `${row.price} €`,
        sortable: true,
        sortAccessor: ({ row }) => row.price,
        cellProps: { align: 'right', sx: { whiteSpace: 'nowrap' } },
        headerCellProps: { sx: { minWidth: 110, whiteSpace: 'nowrap' } },
      },
      {
        ...rowActionsColumn<InternalPackage, CustomProps>({
          actions: ({ row, customTableProps }) => [
            { type: 'edit', label: t('internal-csm:plans_edit_package'), onClick: () => customTableProps?.onEdit(row) },
            { type: 'delete', label: t('internal-csm:plans_delete_package'), onClick: () => customTableProps?.onDelete(row) },
          ],
        }),
        headerCellProps: { sx: { minWidth: 110 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (planPackage) => setDialogState({ open: true, planPackage }),
      onDelete: (planPackage) =>
        setConfirmDialogData({
          title: t('internal-csm:plans_delete_package'),
          confirmText: t('internal-csm:plans_delete_package_confirm', { name: planPackage.name }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: { action: 'delete', planPackage },
        }),
    }),
    [t]
  )

  const reportStored = (sent: Parameters<typeof getUnstoredPackageFields>[0], stored?: InternalPackage) => {
    if (!stored) {
      notifyError(t('internal-csm:plans_package_not_found_after_save', { name: sent.name }))
      return false
    }
    const unstored = getUnstoredPackageFields(sent, stored)
    if (unstored.length) {
      notifyError(t('internal-csm:plans_not_stored', { fields: unstored.map((field) => t(packageFieldLabelKeys[field])).join(', ') }))
      return false
    }
    return true
  }

  const handleSave = async (values: PackageFormValues) => {
    const stored = dialogState.planPackage
    if (stored) {
      setConfirmDialogData({
        title: t('internal-csm:plans_update_package'),
        confirmText: (
          <Stack spacing={2}>
            <Typography>{t('internal-csm:plans_update_package_confirm', { name: stored.name })}</Typography>
            <Alert severity="warning">{t('internal-csm:plans_package_update_warning')}</Alert>
          </Stack>
        ),
        actionText: t('internal-csm:plans_update_package'),
        data: { action: 'update', planPackage: toPackageUpdate(stored, values) },
      })
      return
    }
    const planPackage = toPackageCreate(values)
    const created = await run(createPackage(planPackage).unwrap(), failureMessages('internal-csm:plans_package_save_failed'))
    if (!created.ok || !reportStored(planPackage, created.result.stored)) return
    notifySuccess(t('internal-csm:plans_package_added', { name: planPackage.name }))
    setDialogState({ open: false })
  }

  const handleConfirm = async (confirmed?: ConfirmDialogData<ConfirmData>) => {
    setConfirmDialogData(undefined)
    const data = confirmed?.data
    if (!data) return
    if (data.action === 'update') {
      const updated = await run(updatePackage(data.planPackage).unwrap(), failureMessages('internal-csm:plans_package_save_failed'))
      if (!updated.ok || !reportStored(data.planPackage, updated.result.stored)) return
      notifySuccess(t('internal-csm:plans_package_updated', { name: data.planPackage.name }))
      setDialogState({ open: false })
      return
    }
    const deleted = await run(deletePackage(data.planPackage.id).unwrap(), failureMessages('internal-csm:plans_package_delete_failed'))
    if (!deleted.ok) return
    if (deleted.result.stored) {
      notifyError(t('internal-csm:plans_package_not_deleted', { name: data.planPackage.name }))
      return
    }
    notifySuccess(t('internal-csm:plans_package_deleted', { name: data.planPackage.name }))
  }

  const isLoading = packagesQuery.isLoading || rolesQuery.isLoading || modulesQuery.isLoading
  const isError = packagesQuery.isError || rolesQuery.isError || modulesQuery.isError

  return (
    <Box>
      <InternalListToolbar add={{ label: t('internal-csm:plans_add_package'), onClick: () => setDialogState({ open: true }), disabled: isLoading || isError }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={roleFilter}
          onChange={(_, value: PackageRoleFilter | null) => value && setFilter(selectedRole ?? null, value)}
          aria-label={t('internal-csm:plans_role_filter')}
        >
          <ToggleButton value="with">{t('internal-csm:plans_has_role')}</ToggleButton>
          <ToggleButton value="without">{t('internal-csm:plans_has_no_role')}</ToggleButton>
        </ToggleButtonGroup>
        <Autocomplete
          size="small"
          options={roleNames}
          value={selectedRole ?? null}
          onChange={(_, value) => setFilter(value, roleFilter)}
          renderInput={(params) => <TextField {...params} label={t('internal-csm:plans_role_filter')} placeholder={t('internal-csm:plans_any_role')} />}
          sx={{ minWidth: 280 }}
        />
        <Typography variant="body2" color="text.secondary">
          {t('internal-csm:plans_package_count', { count: rows.length, total: packages.length })}
        </Typography>
      </InternalListToolbar>

      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-csm:plans_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <PackageEditDialog
        open={dialogState.open}
        planPackage={dialogState.planPackage}
        packages={packages}
        roles={roles}
        modules={modules}
        isSaving={isCreating || isUpdating}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Box>
  )
}
